import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import _ from 'lodash';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, finalize, first, switchMap, take, tap } from 'rxjs/operators';
import { Uuid } from '../../../../../../graphql';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { DialogParams, DialogService } from '../../../../../core/services/dialog.service';
import { InvoiceSharingService } from '../../../../../core/services/invoice-sharing.service';
import { LaunchDarklyService } from '../../../../../core/services/launchdarkly.service';
import { FeatureService } from '../../../../../core/services/permission/feature.service';
import { PermissionService } from '../../../../../core/services/permission/permission.service';
import { Round } from '../../../../../data/logic/math.functions';
import { DocumentTypes, Job, JobPayment } from '../../../../../data/models';
import { IntegrationProvider } from '../../../../../data/models/integrations.model';
import { RepoJobsService } from '../../../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { ReportService } from '../../../../../reports/report.service';
import { moveInArray } from '../../../../../utils/helper-functions/array.functions';
import { AccountIntegrationsStoresService } from '../../../../account';
import { CustomLayoutsService } from '../../../../estimates/custom-layouts/custom-layouts.service';
import { InvoicesReceiptService } from '../../../invoices/invoices-receipt.service';
import { InvoicesService } from '../../../invoices/invoices.service';
import { InvoicingService } from '../invoicing.service';

@Component({
    selector: 'app-job-invoices-contract-price',
    templateUrl: './job-invoices-contract-price.component.html',
    styleUrls: [
        '../../../../../../../src/app/shared/composable-styles/input.scss',
        '../../../../../../../src/app/shared/composable-styles/button.scss',
        './job-invoices-contract-price.component.scss',
    ],
    standalone: false,
})
export class JobInvoicesContractPriceComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() job: Job;
    @Output() invoiceChange = new EventEmitter();
    taxCode = '';
    tableHeight = 'auto';
    lastSharedInvoiceStatus = '';
    generatingPDF = false;
    pdfUrl = '';
    paymentTotal = 0;
    adjustedTotal = 0;
    remainingTotal = 0;
    remainingPercentage = 0;
    paymentPercentage = 0;
    invoiceTemplatePresets: string | null = null;
    showSyncExternalPayments = false;
    accountingProvider: IntegrationProvider | null = null;
    paymentSyncRunning = false;
    #usingAdjustments: boolean;

    jobPayments = signal<JobPayment[]>([]);
    statusFilter = signal('All');
    searchText = signal<string>('');

    rows = computed(() => {
        if (this.statusFilter() == 'All') {
            return this.searchFilteredInvoices();
        }

        const statusFilteredInvoices = this.searchFilteredInvoices().filter((invoice) => {
            return this.statusFilter() == invoice.Status;
        });

        return statusFilteredInvoices;
    });

    searchFilteredInvoices = computed(() => {
        const searchTerm = this.searchText().toLowerCase();
        if (searchTerm.length > 0) {
            const searchFilteredOrders =
                this.jobPayments()?.filter((payment) => {
                    return (
                        payment.Description.toLowerCase().includes(searchTerm) ||
                        payment.InvoiceNumber.toString().toLowerCase().includes(searchTerm)
                    );
                }) ?? [];

            return searchFilteredOrders;
        }

        return this.jobPayments();
    });

    invoiceCounts = computed(() => {
        const counts = {
            all: 0,
            pending: 0,
            invoiced: 0,
            partreceived: 0,
            received: 0,
        };

        this.searchFilteredInvoices().forEach((p) => {
            counts.all++;
            switch (p.Status) {
                case 'Pending':
                    counts.pending++;
                    break;
                case 'Invoiced':
                    counts.invoiced++;
                    break;
                case 'PartReceived':
                    counts.partreceived++;
                    break;
                case 'Received':
                    counts.received++;
                    break;
            }
        });

        return counts;
    });

    constructor(
        private reportService: ReportService,
        private bxUnverifiedAlertService: BxUnverifiedAlertService,
        private invoiceSharingService: InvoiceSharingService,
        public permissionService: PermissionService,
        public featureService: FeatureService,
        private logger: BxLogger,
        private invoicesService: InvoicesService,
        private invoicesReceiptService: InvoicesReceiptService,
        private customLayoutsService: CustomLayoutsService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private dialogService: DialogService,
        private accountIntegrationsStore: AccountIntegrationsStoresService,
        private invoicingService: InvoicingService,
        private lds: LaunchDarklyService,
        private bxLogger: BxLogger
    ) {
        this.#usingAdjustments =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';
    }

    ngOnInit(): void {
        this.accountIntegrationsStore.getAccountingIntegration$
            .pipe(
                filter((ip): ip is NonNullable<typeof ip> => !!ip),
                first(),
                tap(({ provider }) => {
                    this.accountingProvider = provider;
                    this.showSyncExternalPayments =
                        (this.lds.get('accounting-sync-external-xero-payments') ?? false) &&
                        this.accountingProvider == IntegrationProvider.Xero;
                })
            )
            .subscribe();

        this.taxCode = this.job.CalculationMethod === 0 ? this.sharedData.UserData.tenant.TaxCode : this.job.TaxRatesShortCode;
        this.load();
    }

    handleSearchInput(event: Event) {
        const searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.searchText.set(searchTerm);
    }

    setInvoiceTemplatePresetData() {
        let order = 0;
        if (!this.rows()?.length) this.invoiceTemplatePresets = null;
        const invoices = _.filter(this.rows(), { IsDeleted: false });
        const data = _.orderBy(invoices, ['CreationDate']).map((i) => {
            order++;
            return {
                description: i.Description,
                order,
                percent: i.PercentageOfTotal,
                comment: i.Comment,
                isDepositInvoice: i.IsDepositInvoice,
            };
        });
        // @ts-ignore TS2322
        this.invoiceTemplatePresets = JSON.stringify(data);
    }

    syncExternalPayments() {
        const errorMsg = `Failed to synchronise payments from ${this.accountingProvider}`;
        of(null)
            .pipe(
                tap(() => (this.paymentSyncRunning = true)),
                switchMap(() => this.invoicingService.syncExternalInvoicePayments(this.job.ID_Job as Uuid, this.accountingProvider!)),
                take(1),
                catchError((error: any) => {
                    this.bxLogger.error(errorMsg);
                    return EMPTY;
                }),
                finalize(() => {
                    this.paymentSyncRunning = false;
                })
            )
            .subscribe((result) => {
                if (!result.data?.syncExternalInvoicePayments.boolean) {
                    this.bxLogger.error(errorMsg);
                    return;
                }

                this.bxLogger.success(`Payments synchronised from ${this.accountingProvider}`);
            });
    }

    load() {
        this.loadInvoices();
        this.setInvoiceTemplatePresetData();
    }

    loadInvoices() {
        this.jobPayments.set(
            this.job.JobPayments?.filter((p) => {
                return !p.IsDeleted && !p.IsDepositInvoice && !p.IsRetentionInvoice;
            }).sort((a, b) => a.PaymentOrder - b.PaymentOrder) ?? []
        );
    }

    saveJob() {
        if (this.job.InvoiceDueDays || this.job.InvoiceDueDays === 0) this.repoJobs.Save(this.job, 'Default due days updated');
    }

    viewInvoice(invoice: JobPayment) {
        this.invoicesService.viewInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            (error) => ({})
        );
    }

    addInvoice() {
        this.invoicesService.newInvoice(this.job).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    deleteInvoice(evt: MouseEvent, invoice: JobPayment) {
        evt.stopPropagation();
        const params = {
            title: 'Delete invoice?',
            text: "If you go ahead, you won't be able to get it back.",
        };

        this.dialogService.show(params).then(
            () => {
                invoice.IsDeleted = true;
                this.repoJobs.Save(null, 'Invoice has been deleted');
                this.invoiceChange.emit();
                this.load();
            },
            () => ({})
        );
    }

    printJobClaim() {
        const reportName = this.#usingAdjustments ? 'jobclaimv2' : 'jobclaim';
        const params = {
            reportName: reportName,
            reportTitle: 'Invoice Statement',
            params: {
                ID_Job: this.job.ID_Job,
                CalculationMethod: this.job.CalculationMethod,
            },
            fileName: this.job.Number ? this.job.Number + '-InvoiceStatement' : '',
        };
        this.reportService.generate(params);
    }

    printInvoice(invoice: JobPayment) {
        this.invoicesService.generateInvoice(invoice, undefined, true);
    }

    shareInvoice(invoice: JobPayment) {
        this.lastSharedInvoiceStatus = invoice.Status;
        const documentSource = { Id: this.job.ID_Job, Type: 'Job' };
        this.invoiceSharingService.getEmailConfig(invoice, 'pdf').then((emailConfig) => {
            const shareConfig = this.invoiceSharingService.getShareConfig(invoice);
            this.customLayoutsService.openDigitalAcceptanceShareForm(
                emailConfig,
                shareConfig,
                null,
                true,
                DocumentTypes.Invoice,
                false,
                this.job.ID_Lead,
                documentSource,
                true,
                invoice,
                () => {
                    this.handleShareSuccess();
                },
                () => {
                    this.resetInvoiceStatus(invoice);
                }
            );
        });
    }

    // TODO:: move to shared component to slim the product
    emailJobClaim() {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailJobClaim(this.job);
    }

    // TODO:: move to shared component to slim the product
    emailInvoice(invoice: JobPayment) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    termsAndConditions() {
        this.invoicesService.viewTermsAndConditions();
    }

    async addReceipt(invoice: JobPayment) {
        const i = await this.invoicesService.loadInvoice(invoice);
        this.invoicesReceiptService.addReceipt(i).then(
            () => {
                this.repoJobs.Save(null, 'Receipt saved');
                this.invoiceChange.emit();
            },
            () => {
                invoice.entityAspect.rejectChanges();
            }
        );
    }

    async changeInvoiceOrder($event: CdkDragDrop<any>) {
        if ($event.previousIndex !== $event.currentIndex) {
            const nextPayments = this.jobPayments() ?? [];
            const dragItem = nextPayments[$event.previousIndex];
            const dropItem = nextPayments[$event.currentIndex];

            moveInArray(nextPayments, dragItem, dropItem);
            for (let i = 0; i < nextPayments.length; i++) {
                nextPayments[i]!.PaymentOrder = i;
            }

            await this.repoJobs.Save(this.job.JobPayments);
        }
    }

    handleShareSuccess() {
        this.logger.success('Invoice Shared');
    }

    resetInvoiceStatus(invoice: JobPayment) {
        if (this.lastSharedInvoiceStatus !== invoice.Status) {
            invoice.Status = this.lastSharedInvoiceStatus;
            this.invoicesService.setInvoiceNumber(invoice).then((invoiceWithId) => {
                this.invoicesService.saveInvoice(invoiceWithId);
                this.invoiceChange.emit();
            });
        }
    }

    applyTemplate(template: string) {
        const params: DialogParams = {
            title: 'Overwrite your existing invoices?',
            text: 'The selected preset will be applied to this job',
            type: 'warning',
        };
        this.dialogService.show(params).then(
            () => {
                const invoices = JSON.parse(template);
                // Remove current invoices
                // @ts-ignore TS2532
                this.job.JobPayments.forEach((payment) => {
                    payment.IsDeleted = true;
                });
                for (let i = 0; i < invoices.length; i++) {
                    const invoice = invoices[i];
                    const newInvoice = this.repoJobs.CreateInvoice(this.job, {
                        Description: invoice.description,
                        PaymentOrder: invoice.order,
                        PercentageOfTotal: invoice.percent,
                        Comment: invoice.comment,
                        IsDepositInvoice: invoice.isDepositInvoice,
                    });
                    newInvoice.PaymentValue = Round((newInvoice.PercentageOfTotal / 100) * this.job.ContractTotal, 2);
                }
                this.repoJobs.Save(null, 'Preset applied').then(() => {
                    this.load();
                    this.invoiceChange.emit();
                });
            },
            () => {}
        );
    }

    onInvoiceChange() {
        this.invoiceChange.emit();
    }
}
